<template>
  <v-container class="dashboard-page toggle animated" fluid>
    <v-row dense align="stretch">
      <v-col cols="12">
        <div class="dashboard-header">
          <div>
            <!-- <div class="text-h5 font-weight-bold dashboard-title">Dashboard</div>
            <div class="text-body-2 dashboard-subtitle">Users, products, and purchase activity</div> -->
          </div>

          <v-btn
            :loading="loading"
            prepend-icon="mdi-refresh"
            color="primary"
            variant="elevated"
            class="rounded-lg"
            @click="init"
          >
            Refresh
          </v-btn>
        </div>
      </v-col>

      <v-col
        v-for="card in summaryCards"
        :key="card.title"
        cols="12"
        sm="6"
        lg="3"
      >
        <v-card class="metric-card" elevation="2">
          <v-card-text>
            <div class="metric-topline">
              <v-avatar :color="card.color" rounded="lg" size="42">
                <v-icon color="white">{{ card.icon }}</v-icon>
              </v-avatar>
              <v-chip
                :color="card.color"
                size="small"
                variant="tonal"
                class="metric-label-chip"
              >
                {{ card.label }}
              </v-chip>
            </div>
            <div class="metric-value">{{ formatNumber(card.value) }}</div>
            <div class="metric-title">{{ card.title }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="8">
        <v-card class="chart-card" elevation="2">
          <v-card-title class="chart-title">
            <v-icon class="mr-2">mdi-chart-bar</v-icon>
            Purchase Statuses
          </v-card-title>
          <v-card-text>
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="primary"
              height="5"
              class="mb-4 rounded-lg"
            ></v-progress-linear>
            <div class="bar-chart-wrap">
              <canvas id="purchaseStatusChart"></canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card class="chart-card" elevation="2">
          <v-card-title class="chart-title">
            <v-icon class="mr-2">mdi-chart-donut</v-icon>
            Purchase Mix
          </v-card-title>
          <v-card-text>
            <div class="donut-chart-wrap">
              <canvas id="purchaseMixChart"></canvas>
            </div>

            <div class="status-list mt-4">
              <div
                v-for="status in purchaseStatuses"
                :key="status.key"
                class="status-row"
              >
                <div class="status-label">
                  <span
                    class="status-dot"
                    :style="{ backgroundColor: status.color }"
                  ></span>
                  <span>{{ status.label }}</span>
                </div>
                <strong>{{ formatNumber(status.value) }}</strong>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="overview-card" elevation="2">
          <v-card-title class="chart-title">
            <v-icon class="mr-2">mdi-poll</v-icon>
            Store Overview
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <div class="overview-item">
                  <span>Total users</span>
                  <strong>{{ formatNumber(dashboard.users) }}</strong>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="overview-item">
                  <span>Active products</span>
                  <strong>{{ formatNumber(dashboard.products.active) }}</strong>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="overview-item">
                  <span>Total purchases</span>
                  <strong>{{ formatNumber(dashboard.purchases.all) }}</strong>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="snackbar"
      :timeout="2500"
      color="secondary"
      location="top"
    >
      <v-icon>mdi-alert-circle</v-icon>
      <span class="ml-2">{{ snackText }}</span>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import Chart from "chart.js";
import { defineComponent, nextTick } from "vue";

const EMPTY_DASHBOARD = {
  users: 0,
  products: {
    active: 0,
  },
  purchases: {
    all: 0,
    completed: 0,
    pending: 0,
    inprogress: 0,
    refunded: 0,
    cancelled: 0,
    declined: 0,
  },
};

export default defineComponent({
  name: "Dashboard",
  data() {
    return {
      dashboard: JSON.parse(JSON.stringify(EMPTY_DASHBOARD)),
      loading: false,
      snackbar: false,
      snackText: null,
      purchaseStatusChart: null,
      purchaseMixChart: null,
    };
  },
  computed: {
    summaryCards() {
      return [
        {
          title: "Registered Users",
          label: "Users",
          value: this.dashboard.users,
          icon: "mdi-account-group",
          color: "#8a7130",
        },
        {
          title: "Active Products",
          label: "Products",
          value: this.dashboard.products.active,
          icon: "mdi-storefront",
          color: "#b59637",
        },
        {
          title: "Total Purchases",
          label: "Orders",
          value: this.dashboard.purchases.all,
          icon: "mdi-cart",
          color: "#f6d84a",
        },
        {
          title: "Completed Purchases",
          label: "Done",
          value: this.dashboard.purchases.completed,
          icon: "mdi-check-circle",
          color: "#5d4a1c",
        },
      ];
    },
    purchaseStatuses() {
      const purchases = this.dashboard.purchases;

      return [
        {
          key: "completed",
          label: "Completed",
          value: purchases.completed,
          color: "#7ec850",
        },
        {
          key: "pending",
          label: "Pending",
          value: purchases.pending,
          color: "#f6d84a",
        },
        {
          key: "inprogress",
          label: "In Progress",
          value: purchases.inprogress,
          color: "#f0a830",
        },
        {
          key: "refunded",
          label: "Refunded",
          value: purchases.refunded,
          color: "#e07438",
        },
        {
          key: "cancelled",
          label: "Cancelled",
          value: purchases.cancelled,
          color: "#c04830",
        },
        {
          key: "declined",
          label: "Declined",
          value: purchases.declined,
          color: "#8b3020",
        },
      ];
    },
  },
  methods: {
    async init() {
      const token = localStorage.getItem("seham-admin-accesstoken");

      if (!token) {
        this.clearSession();
        return;
      }

      this.loading = true;

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/api/admin/dashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
            },
          },
        );

        this.dashboard = this.normalizeDashboard(res.data.data);
        await nextTick();
        this.renderCharts();
      } catch (error) {
        this.handleRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    normalizeDashboard(data = {}) {
      return {
        users: data.users || 0,
        products: {
          active: data.products?.active || 0,
        },
        purchases: {
          all: data.purchases?.all || 0,
          completed: data.purchases?.completed || 0,
          pending: data.purchases?.pending || 0,
          inprogress: data.purchases?.inprogress || 0,
          refunded: data.purchases?.refunded || 0,
          cancelled: data.purchases?.cancelled || 0,
          declined: data.purchases?.declined || 0,
        },
      };
    },
    renderCharts() {
      this.renderPurchaseStatusChart();
      this.renderPurchaseMixChart();
    },
    renderPurchaseStatusChart() {
      const canvas = document.getElementById("purchaseStatusChart");
      if (!canvas) return;

      if (this.purchaseStatusChart) {
        this.purchaseStatusChart.destroy();
      }

      this.purchaseStatusChart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.purchaseStatuses.map((status) => status.label),
          datasets: [
            {
              label: "Purchases",
              data: this.purchaseStatuses.map((status) => status.value),
              backgroundColor: this.purchaseStatuses.map(
                (status) => status.color,
              ),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  precision: 0,
                },
                gridLines: {
                  color: "rgba(246, 216, 74, 0.12)",
                },
              },
            ],
            xAxes: [
              {
                gridLines: {
                  display: false,
                },
              },
            ],
          },
        },
      });
    },
    renderPurchaseMixChart() {
      const canvas = document.getElementById("purchaseMixChart");
      if (!canvas) return;

      if (this.purchaseMixChart) {
        this.purchaseMixChart.destroy();
      }

      const values = this.purchaseStatuses.map((status) => status.value);
      const hasData = values.some((value) => value > 0);

      this.purchaseMixChart = new Chart(canvas.getContext("2d"), {
        type: "doughnut",
        data: {
          labels: hasData
            ? this.purchaseStatuses.map((status) => status.label)
            : ["No purchases"],
          datasets: [
            {
              data: hasData ? values : [1],
              backgroundColor: hasData
                ? this.purchaseStatuses.map((status) => status.color)
                : ["#27301f"],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 68,
          legend: {
            display: false,
          },
        },
      });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    handleRequestError(error) {
      if (error.response?.status === 401) {
        this.clearSession();
        return;
      }

      this.snackbar = true;
      this.snackText = error.response
        ? `Error: ${error.response.status}`
        : "No response received";
    },
    clearSession() {
      localStorage.removeItem("vuex");
      localStorage.removeItem("seham-admin-accesstoken");
      window.location.reload();
    },
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    if (this.purchaseStatusChart) {
      this.purchaseStatusChart.destroy();
    }

    if (this.purchaseMixChart) {
      this.purchaseMixChart.destroy();
    }
  },
});
</script>

<style scoped>
.dashboard-page {
  font-family:
    Saira Condensed,
    sans-serif;
  color: var(--seham-text);
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.dashboard-title,
.chart-title {
  font-family:
    Russo One,
    sans-serif;
  letter-spacing: 0;
}

.dashboard-subtitle,
.metric-title,
.status-row,
.overview-item span {
  color: var(--seham-muted);
}

.metric-card,
.chart-card,
.overview-card {
  height: 100%;
  border-radius: 8px;
}

.metric-card {
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 82% 14%,
      rgba(246, 216, 74, 0.2),
      transparent 34%
    ),
    linear-gradient(135deg, rgba(181, 150, 55, 0.1), transparent 55%);
  pointer-events: none;
}

.metric-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.metric-value {
  margin-top: 22px;
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  color: var(--seham-yellow-soft);
}

.metric-title {
  margin-top: 8px;
  font-size: 1rem;
}

.metric-label-chip {
  color: #ffffff !important;
  font-weight: 700;
}

.bar-chart-wrap {
  height: 340px;
  position: relative;
}

.donut-chart-wrap {
  height: 250px;
  position: relative;
}

.status-list {
  display: grid;
  gap: 10px;
}

.status-row,
.status-label,
.overview-item {
  display: flex;
  align-items: center;
}

.status-row,
.overview-item {
  justify-content: space-between;
}

.status-label {
  gap: 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex: 0 0 10px;
  box-shadow: 0 0 14px currentColor;
}

.overview-item {
  min-height: 76px;
  padding: 16px;
  border: 1px solid var(--seham-line);
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(10, 9, 6, 0.94),
    rgba(246, 216, 74, 0.08)
  );
}

.overview-item strong {
  font-size: 1.75rem;
  line-height: 1;
  color: var(--seham-yellow);
}

@media (max-width: 600px) {
  .dashboard-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .metric-value {
    font-size: 2rem;
  }

  .bar-chart-wrap {
    height: 300px;
  }
}
</style>
